import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryKey
} from "@tanstack/react-query";
import { CertificateInfo } from "../components/CertificateButton/CertificateButtonContainer.types";
import { ERRORS } from "../consts";
import { fetcherFactory } from "../services";
import { useAuthStore, useLanguageStore } from "../stores";
// import type { NotificationResponse } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function getEditProfileImageQuery(
  refreshSession: () => Promise<void>,
  {
    enabled,
    idToken,
    queryKey,
    sessionToken,
    uri
  } : {
    enabled: boolean
    idToken: string
    queryKey: string[]
    sessionToken: string
    uri: string
  })
{
  const fetcher = fetcherFactory();
  return {
    enabled:enabled,
    // initialData,
    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    )
      .then((res) => {
        return res.text();
      })
      .catch((error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          refreshSession();
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }),
    queryKey: queryKey,
    staleTime: 2 * 60 * 1000 // 2min
  };
}

export function useGetProfileImageQuery({
  enabled=true
  // queryKey=[]
} : {
  enabled?: boolean
  // queryKey?: string[]
}) {
  const fetcher = fetcherFactory();
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user);
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const language = useLanguageStore(state => state.language)?.id ?? "";
  const setProfileImage = useAuthStore(state => state.setProfileImage);

  const uri = `/mediamanager/userprofile/${userId}/image`;

  return useQuery<
    unknown,
    unknown,
    string,
    QueryKey
  >(
    {
      enabled:Boolean(enabled
        && userId
        && idToken
        && sessionToken
      ) && !isSeedingSession,
      // initialData,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers:{
            "Accept-Language": language,
            "authorization": idToken,
            "x-ada-session-token": sessionToken
          }
        }
      )
        .then((res) => {
          const image = res.text();
          image.then(text => setProfileImage(text));
          return image;
        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [uri, isSeedingSession],
      refetchOnMount: true,
      staleTime: 60 * 60 * 1000 // 1h
    });
}

export function useMutationProfileImageQuery() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userData = useAuthStore(state => state.userData);
  const userId = userData!?.id_user;

  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const language = useLanguageStore(state => state.language)?.id ?? "";
  const fetcher = fetcherFactory();
  const queryClient = useQueryClient();

  const apiPath = `/mediamanager/userprofile/image?userId=${userId}`;
  const uriToInvalidate = `/mediamanager/userprofile/${userId}/image`;

  return useMutation({
    mutationFn: (formdata:FormData) => {
      return fetcher(
        `${basePath}${apiPath}`,
        {
          body: formdata,
          headers:{
            "Accept-Language": language,
            "authorization": idToken,
            "x-ada-session-token": sessionToken

          },
          method: "POST"
        }
      )
        .then((res) => {
          return res.text();
        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [uriToInvalidate, isSeedingSession] });
    }
  });
}

export function useDeleteProfileImageQuery() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userData = useAuthStore(state => state.userData);
  const userId = userData!?.id_user;
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const corporateId = corporateInfo?.id;

  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const language = useLanguageStore(state => state.language)?.id ?? "";
  const fetcher = fetcherFactory();
  const queryClient = useQueryClient();

  const apiPath = `/mediamanager/userprofile/${userId}/image?corporateId=${corporateId}`;
  const uriToInvalidate = `/mediamanager/userprofile/${userId}/image`;

  return useMutation({
    mutationFn: () => {
      return fetcher(
        `${basePath}${apiPath}`,
        {
          headers:{
            "Accept-Language": language,
            "authorization": idToken,
            "x-ada-session-token": sessionToken

          },
          method: "DELETE"
        }
      )
        .then((res) => {

          return res.text();
        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [uriToInvalidate, isSeedingSession] });
    }
  });
}

export function useCvFormativeQuery({
  enabled=true,
  studentId
}: {
  enabled?: boolean
  studentId: string
}) {
  const fetcher = fetcherFactory();

  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";

  return useQuery<
    unknown,
    unknown,
    string,
    QueryKey
  >({
    enabled: Boolean(
      enabled
      && sessionToken
      && corporateId
      && initiativeId
      && studentId
    ),
    queryFn: () => fetcher(
      `${basePath}/enrollment/cvformative?studentId=${studentId}`,
      {
        body: null,
        headers:{
          "authorization": idToken,
          "Content-Type": "application/json",
          "x-ada-session-token": sessionToken
        },
        method: "GET"
      }
    )
      .then((res) => {
        return res.text();
      })
      .catch((error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }),
    queryKey: ["useCvFormativeQuery", studentId]
  });
}

export function useDownloadCertificate() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  return useMutation({
    mutationFn: (certificateInfo: CertificateInfo) => fetcher(
      `${basePath}/certificates/issued-certificate/${certificateInfo.certificateId}/download?objectId=${certificateInfo.learningObjectId}&learnerId=${certificateInfo.learnerId}`,
      {
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        },
        method: "GET"
      }).then( (res) => {
      return res.blob();
    })
      .catch(async (error) => {
        const errorRes = await error;
        throw errorRes;
      })
  });
}

export function useStartSignCertificate() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const corporateId = useAuthStore(state => state.corporateInfo?.id)??"";
  const fetcher = fetcherFactory();
  return useMutation<{ sign_url: string, job_id: string }, unknown, CertificateInfo>({
    mutationFn: (certificateInfo: CertificateInfo) => fetcher(
      `${basePath}/certificate/issued-certificate/${certificateInfo.certificateId}/sign`,
      {
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken,
          "content-type":"application/json"
        },
        method: "POST",
        body: JSON.stringify({
          "user_id": certificateInfo.learnerId,
          "certificate_id": certificateInfo.certificateId,
          "learning_object_id": certificateInfo.learningObjectId,
          "corporate_id": corporateId
        })
      }).then( (res) => {
      return res.json();
    })
      .catch(async (error) => {
        const errorRes = await error;
        throw errorRes;
      })
  });
}

export function useEndSignCertificate() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const corporateId = useAuthStore(state => state.corporateInfo?.id)??"";
  const fetcher = fetcherFactory();
  return useMutation({
    mutationFn: (certificateInfo: CertificateInfo & { jobId: string}) => fetcher(
      `${basePath}/certificate/issued-certificate/${certificateInfo.certificateId}/sign`,
      {
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken,
          "content-type":"application/json"
        },
        method: "PUT",
        body: JSON.stringify({
          "user_id": certificateInfo.learnerId,
          "certificate_id": certificateInfo.certificateId,
          "learning_object_id": certificateInfo.learningObjectId,
          "job_id": certificateInfo.jobId,
          "corporate_id": corporateId
        })
      }).then( () => {
      return undefined;
    })
      .catch(async (error) => {
        const errorRes = await error;
        throw errorRes;
      })
  });
}
